import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { usePortalClient } from './usePortalClient';
import { money, date, invoiceTotal } from '../shared/formatters';
import StatusPill from '../shared/components/StatusPill';
import Breadcrumbs from '../shared/components/Breadcrumbs';
import { useToast } from '../shared/components/ToastProvider';
import { portalApi } from '../lib/api';

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const { data, add } = useData();
  const { base, client } = usePortalClient();
  const { notify } = useToast();
  const [downloading, setDownloading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [paying, setPaying] = useState(false);
  const invoice = data.invoices.find((item) => item.id === invoiceId) || data.invoices[0];
  const isInvoice = (invoice.docType || 'invoice') === 'invoice';

  const existingClaim = data.payments.find(
    (p) => p.invoice_id === invoice.id && p.status === 'pending'
  );

  const download = async () => {
    setDownloading(true);
    try {
      await portalApi.downloadInvoicePdf(invoice.id, `${invoice.number || 'document'}.pdf`);
    } catch (err) {
      notify(err.message || 'PDF download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // Pass 6 §6E — card payment via Stripe Checkout (tenant's Connect
  // account). Returns a hosted checkout URL; the webhook marks the
  // invoice paid and creates the payments row.
  const payNow = async () => {
    setPaying(true);
    try {
      const result = await portalApi.createCheckoutSession(invoice.id);
      window.location = result.url;
    } catch (err) {
      notify(err.message || 'Could not start the payment', 'error');
      setPaying(false);
    }
  };

  const markPaidByBacs = async () => {
    setClaiming(true);
    try {
      await add('payments', {
        invoice_id: invoice.id,
        amount: invoiceTotal(invoice),
        method: 'bank_transfer',
        status: 'pending',
        date: new Date().toISOString().slice(0, 10),
        reference: `BACS claim by ${client.name}`,
      });
      notify('Thanks — we have been notified and will confirm your payment shortly.');
    } catch (err) {
      notify(err.message || 'Could not submit the claim', 'error');
    } finally {
      setClaiming(false);
    }
  };

  const label = invoice.docType === 'quote' ? 'Quotation' : invoice.docType === 'estimate' ? 'Estimate' : 'Invoice';

  return <div className="portal-card invoice-detail"><Breadcrumbs items={[{ label: `${label}s`, to: `${base}/${invoice.docType === 'quote' ? 'quotes' : invoice.docType === 'estimate' ? 'estimates' : 'invoices'}` }, { label: invoice.number }]} /><div className="panel-head"><div><span className="eyebrow">{label}</span><h1>{invoice.number}</h1></div><StatusPill invoice={invoice} /></div><p>{invoice.title}</p><table><thead><tr><th>Description</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead><tbody>{invoice.line_items.map((item) => <tr key={item.id}><td>{item.description}</td><td>{item.quantity}</td><td>{money(item.unit_price)}</td><td>{money(item.total)}</td></tr>)}</tbody></table><div className="invoice-total"><span>Total</span><strong>{money(invoiceTotal(invoice))}</strong></div><p>Due {date(invoice.due_date)}</p><div className="invoice-actions"><button className="secondary-button" onClick={download} disabled={downloading}>{downloading ? 'Preparing PDF…' : `Download ${label} PDF`}</button>{isInvoice && invoice.status !== 'paid' && (existingClaim ? <span className="status-pill pending">Payment pending confirmation</span> : <><button className="primary-button" onClick={payNow} disabled={paying || claiming}>{paying ? 'Opening payment…' : 'Pay now with card'}</button><button className="secondary-button" onClick={markPaidByBacs} disabled={claiming || paying}>{claiming ? 'Sending…' : 'I have paid this by BACS'}</button></>)}</div><Link className="back-link" to="../invoices">Back</Link></div>;
}
